import { sha256 } from "@noble/hashes/sha2.js";
import { base58, createBase58check } from "@scure/base";
import type { WithdrawAddressFormat } from "@tradingdroid/frontends-types";
import { isAddress } from "viem";

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

// TRON uses base58check (SHA-256 double-hash) with a 0x41 version byte + 20-byte
// payload = 21 bytes after checksum stripping.
const tronBase58Check = createBase58check(sha256);
const TRON_VERSION_BYTE = 0x41;
const TRON_PAYLOAD_BYTES = 21;

// Solana public keys are raw ed25519 keys: exactly 32 bytes encoded in base58.
const SOLANA_PAYLOAD_BYTES = 32;

const validateEvm = (address: string): ValidationResult => {
  if (!address.startsWith("0x")) {
    return { valid: false, error: "EVM address must start with 0x" };
  }
  if (!isAddress(address, { strict: false })) {
    return { valid: false, error: "Invalid EVM address format" };
  }
  // Strict mode enforces EIP-55 checksum when the address is mixed-case
  if (/[A-F]/.test(address) && /[a-f]/.test(address) && !isAddress(address, { strict: true })) {
    return { valid: false, error: "EVM address checksum (EIP-55) is invalid" };
  }
  return { valid: true };
};

const validateTron = (address: string): ValidationResult => {
  if (!address.startsWith("T")) {
    return { valid: false, error: "TRON address must start with 'T'" };
  }
  let decoded: Uint8Array;
  try {
    decoded = tronBase58Check.decode(address);
  } catch {
    return { valid: false, error: "TRON address checksum is invalid" };
  }
  if (decoded.length !== TRON_PAYLOAD_BYTES) {
    return { valid: false, error: "TRON address has unexpected length" };
  }
  if (decoded[0] !== TRON_VERSION_BYTE) {
    return { valid: false, error: "TRON address has invalid version byte" };
  }
  return { valid: true };
};

const validateSolana = (address: string): ValidationResult => {
  let decoded: Uint8Array;
  try {
    decoded = base58.decode(address);
  } catch {
    return { valid: false, error: "Solana address is not valid base58" };
  }
  if (decoded.length !== SOLANA_PAYLOAD_BYTES) {
    return { valid: false, error: "Solana address must decode to exactly 32 bytes" };
  }
  return { valid: true };
};

export const validateWithdrawAddress = (
  address: string,
  format: WithdrawAddressFormat,
): ValidationResult => {
  if (!address) {
    return { valid: false, error: "Address is required" };
  }
  switch (format) {
    case "evm":
      return validateEvm(address);
    case "tron_base58":
      return validateTron(address);
    case "solana_base58":
      return validateSolana(address);
    default:
      return { valid: false, error: `Unknown address format: ${format}` };
  }
};
